import {
	type AttemptedOperation,
	type PlatformErrorAreas,
	reportPlatformError,
	runWithErrorReporting,
} from "$root/api/error-reporting";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import {
	MIN_NUMBER_OF_INSTRUMENTS,
	MIN_NUMBER_OF_TARGET_INVESTMENT_INSTRUMENTS,
	MIN_NUMBER_OF_UNIVERSE_INSTRUMENTS,
	TARGET_PORTFOLIO_INSTRUMENT_LIMIT,
	UNIVERSE_INSTRUMENT_LIMIT,
} from "$root/functional-areas/const";
import {
	cashTickerToInstrument,
	covertInstrumentEditorToReviewTicker,
	useInstrumentEditorBuilder,
} from "$root/functional-areas/instruments-editor/builder";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import InstrumentCompositionEditor from "$root/functional-areas/instruments-editor/instrumentEditor";
import { spawnCopyCompositionDialog } from "$root/functional-areas/instruments-editor/spawn/copy-composition";
import { createInstrumentList, instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { customerDecimalPlaces, useUserValue } from "$root/functional-areas/user";
import { weightTotalStatus } from "$root/functional-areas/instruments-editor/weight-total";
import { useMappedQueries, useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { columnsDefinition } from "$root/ui-lib/table/column";
import type { CompositionTemplateEntityType } from "@eu.mdotm.api/sphere";
import type { RichAcl } from "@eu.mdotm.api/smart-workspaces";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { identity } from "@mdotm/utils/functions";
import { Button, type Crumb } from "@mdotm/shadcn/mdotm/components";
import { useQuery } from "@tanstack/react-query";
import { OrderedMap } from "immutable";
import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { instrumentsEditorRepository } from "$root/functional-areas/instruments-editor/repository";
import { spawnSubmitUniverseEditDialog } from "./spawn-edit-universe";

async function submitWithTryCath(
	callback: () => Promise<void>,
	opt?: {
		area: PlatformErrorAreas;
		attemptedOperation?: AttemptedOperation;
	},
) {
	try {
		await callback();
	} catch (error) {
		reportPlatformError(error, "ERROR", opt?.area, opt?.attemptedOperation);
		throw error;
	}
}
function ManualEditComposition({
	uuid: _uuid,
	entity: _entity,
	copyComposition,
}: {
	uuid?: string;
	entity?: string;
	copyComposition?: string;
}): React.JSX.Element {
	const uuid = _uuid!;
	const entity = _entity === "REFERENCE" ? "TARGET_INVESTMENT" : "UNIVERSE";
	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>([typedUrlForRoute("Login", {})]);

	const { t } = useTranslation();
	const { push, replace } = useTypedNavigation();
	const user = useUserValue();

	const querySummary = useQueryNoRefetch(["queryInvestmentSummary", uuid], {
		queryFn: async () => {
			const minimunSummary = await match(entity)
				.returnType<Promise<{ name: string; richAcl?: RichAcl }>>()
				.with("TARGET_INVESTMENT", async () => {
					const summary = await portfolioRepository.summary.fetchQuery(uuid, { enhanced: false });
					return { name: summary.name ?? "...", richAcl: summary.richAcl };
				})
				.with("UNIVERSE", async () => {
					const summary = await universeRepository.summary.fetchQuery(uuid);
					return { name: summary.name ?? "...", richAcl: summary.richAcl };
				})
				.exhaustive();

			return minimunSummary;
		},
	});

	useQueryReactor(querySummary, {
		onSuccess({ richAcl }) {
			match(entity)
				.with("TARGET_INVESTMENT", () => {
					const canEditComposition = aclByArea.targetPortfolio.canEditComposition(user.id, richAcl?.acl ?? []);
					if (!canEditComposition) {
						push("ReferenceDetails", { uuid });
					}
				})
				.with("UNIVERSE", () => {
					const canEditComposition = aclByArea.universe.canEditComposition(user.id, richAcl?.acl ?? []);
					if (!canEditComposition) {
						push("UniverseDetails", { universeUuid: uuid });
					}
				})
				.exhaustive();
		},
	});

	const { data: summary } = querySummary;

	const instrumentBuilder = useInstrumentEditorBuilder({ composition: OrderedMap() });
	const compositionMapWithoutDeleteInstruments = useMemo(
		() => instrumentBuilder.getComposition({ removeDeleted: true }),
		[instrumentBuilder],
	);
	const totalCompositionWeight = instrumentBuilder.getTotal("weight");
	const weightTotalPrecision = customerDecimalPlaces(user);

	const queryEditComposition = instrumentsRepository.editComposition.useQuery(uuid, entity);
	const queryUserInstrumentMap = useQuery({
		...instrumentsRepository.userClassifications.getQueryConfig(
			queryEditComposition.data?.composition?.map((entry) => entry.ticker).filter((ticker) => ticker != null) ?? [],
		),
		enabled: queryEditComposition.data != null,
	});

	const queryInvestmentsRichInfo = useQuery({
		...instrumentsRepository.investmentsRichInfo.getQueryConfig(queryEditComposition.data?.composition ?? []),
		enabled: queryEditComposition.data != null,
	});

	const query = useMappedQueries(
		identity,
		[],
		[queryEditComposition, queryUserInstrumentMap, queryInvestmentsRichInfo],
	);

	useQueryReactor(query, {
		onSuccess([compositionWithCash, userInstrumentsMap, investmentsRichInfo]) {
			const instruments = createInstrumentList({
				composition: compositionWithCash.composition ?? [],
				userInstrumentsMap,
				investmentData: investmentsRichInfo,
			});

			instrumentBuilder.reset(
				{
					composition: OrderedMap<string, Instrument>(instruments.map((x) => [x.id, x])),
					cash: compositionWithCash.cashTicker ? cashTickerToInstrument(compositionWithCash.cashTicker) : undefined,
				},
				{ disableToggleDirty: true },
			);
		},
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: <not needed instrumentBuilder.reset>
	useEffect(() => {
		if (copyComposition) {
			spawnCopyCompositionDialog({
				uuid,
				entity: "REFERENCE_INVESTMENT",
				initialSidePanelFilters: {
					search: "",
					filters: [
						{
							key: "TYPE",
							relation: "EQUAL",
							type: "FINITE_SET_TYPE",
							value: ["REFERENCE_INVESTMENT", "INVESTMENT"] satisfies CompositionTemplateEntityType[],
						},
					],
				},
				onSubmitAsync: async (data) => {
					await runWithErrorReporting(
						async () => {
							const { instruments, cashTicker } = await instrumentsRepository.copyComposition.fetchQuery(
								data.uuid,
								data.type,
							);

							instrumentBuilder.reset(({ cash }) => ({
								composition: OrderedMap<string, Instrument>(instruments.map((x) => [x.id, x])),
								cash: cashTicker ? cashTickerToInstrument(cashTicker) : cash,
							}));

							replace("Portfolios/ManualEdit", { uuid, entity });
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "attempt to load entity template",
								payload: JSON.stringify(data),
							},
						},
					);
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [replace, copyComposition, uuid]);

	const forwardToEntityPage = match(entity)
		.returnType<(uuid: string) => void>()
		.with("TARGET_INVESTMENT", () => (uid) => push("ReferenceDetails", { uuid: uid }))
		.with("UNIVERSE", () => (uid) => push("UniverseDetails", { universeUuid: uid }))
		.exhaustive();

	const maxInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? +Infinity
		: match(entity)
				.returnType<number>()
				.with("TARGET_INVESTMENT", () => TARGET_PORTFOLIO_INSTRUMENT_LIMIT)
				.with("UNIVERSE", () => UNIVERSE_INSTRUMENT_LIMIT)
				.exhaustive();

	const minInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? MIN_NUMBER_OF_INSTRUMENTS
		: match(entity)
				.returnType<number>()
				.with("TARGET_INVESTMENT", () => MIN_NUMBER_OF_TARGET_INVESTMENT_INSTRUMENTS)
				.with("UNIVERSE", () => MIN_NUMBER_OF_UNIVERSE_INSTRUMENTS)
				.exhaustive();

	const isSubmitDisabled = useMemo(() => {
		const instrumentsSize = compositionMapWithoutDeleteInstruments.size;
		if (entity === "UNIVERSE") {
			return instrumentsSize < minInstrumentsInComposition || instrumentsSize > maxInstrumentsInComposition;
		}
		return (
			weightTotalStatus(totalCompositionWeight, weightTotalPrecision) !== "exact" ||
			instrumentsSize < minInstrumentsInComposition ||
			instrumentsSize > maxInstrumentsInComposition
		);
	}, [
		compositionMapWithoutDeleteInstruments.size,
		entity,
		maxInstrumentsInComposition,
		minInstrumentsInComposition,
		totalCompositionWeight,
		weightTotalPrecision,
	]);

	const crumbs = useCrumbs();
	const classifications = useAtomValue(userClassifications$);
	const columns = useMemo(
		() =>
			entity === "TARGET_INVESTMENT"
				? columnsDefinition.instrumentComposition(classifications ?? [])
				: columnsDefinition.instrumentUniverseComposition(classifications ?? []),
		[entity, classifications],
	);
	return (
		<>
			<PageHeader
				crumbs={match(entity)
					.returnType<Crumb[]>()

					.with("TARGET_INVESTMENT", () => [
						...crumbs.portfolioStudio("References"),
						...crumbs.referenceName({ uuid, name: summary?.name }),
						...crumbs.editReference,
					])
					.with("UNIVERSE", () => [
						...crumbs.portfolioStudio("Universes"),
						...crumbs.universeName({ uuid, name: summary?.name }),
						...crumbs.editUniverse,
					])
					.exhaustive()}
				titleAction={
					<div className="flex items-center space-x-2">
						<Button
							size="small"
							onClick={match(entity)
								.returnType<() => void>()
								.with("TARGET_INVESTMENT", () => () => push("ReferenceDetails", { uuid }))
								.with("UNIVERSE", () => () => push("UniverseDetails", { universeUuid: uuid }))
								.exhaustive()}
							palette="tertiary"
							data-qualifier="CompositionEditor/Cancel"
						>
							{t("BUTTON.CANCEL")}
						</Button>
						{entity === "UNIVERSE" ? (
							<Button
								palette="primary"
								size="small"
								data-qualifier="CompositionEditor/Save"
								disabled={isSubmitDisabled}
								onClick={() =>
									spawnSubmitUniverseEditDialog({
										async onAsyncSubmit(form) {
											await submitWithTryCath(
												async () => {
													let latestUuid = uuid;
													const composition = covertInstrumentEditorToReviewTicker(
														Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values()),
													);

													const hasPortfolioInComposition = instrumentBuilder
														.getComposition()
														.some((instrument) => instrument.proxyOverwriteType === "PORTFOLIO_MIXED");

													const portfolioSavingMode = hasPortfolioInComposition ? "NEST_PORTFOLIOS" : "MIX_INSTRUMENTS";

													if (form.saveMode === "SAVE_AS_NEW") {
														const { identifier } = await instrumentsEditorRepository.create.universe({
															composition,
															form: { name: form.name, portfolioSavingMode },
														});
														latestUuid = identifier ?? uuid;
													}

													if (form.saveMode === "SAVE") {
														await instrumentsEditorRepository.edit.universe(latestUuid, {
															composition,
															form: { portfolioSavingMode },
														});
													}

													flushSync(() =>
														setPathToNotBlock([typedUrlForRoute("UniverseDetails", { universeUuid: latestUuid })]),
													);
													forwardToEntityPage(latestUuid);
												},
												{
													area: "universe",
													attemptedOperation: { message: `Unable to save the edited universe (${uuid})` },
												},
											);
										},
									})
								}
							>
								{t("BUTTON.SAVE")}
							</Button>
						) : (
							<Button
								size="small"
								disabled={isSubmitDisabled}
								onClickAsync={() =>
									match(entity)
										.with("TARGET_INVESTMENT", async () => {
											await submitWithTryCath(
												async () => {
													const composition = covertInstrumentEditorToReviewTicker(
														Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values()),
													);
													await instrumentsEditorRepository.edit.targetInvestment(uuid, {
														composition,
													});

													flushSync(() => setPathToNotBlock([typedUrlForRoute("ReferenceDetails", { uuid })]));
													forwardToEntityPage(uuid);
												},
												{
													area: "portfolio",
													attemptedOperation: { message: `Unable to save the Reference (${uuid})` },
												},
											);
										})
										.exhaustive()
								}
								palette="primary"
								data-qualifier="CompositionEditor/Save"
							>
								{t("BUTTON.DONE")}
							</Button>
						)}
					</div>
				}
			/>
			<ReactQueryWrapperBase query={query}>
				{() => (
					<div className="min-h-0 grow flex">
						<InstrumentCompositionEditor
							entity={entity}
							uuid={uuid}
							mode="edit"
							instrumentBuilder={instrumentBuilder}
							minThreshold={minInstrumentsInComposition}
							limit={maxInstrumentsInComposition}
							columns={columns}
						/>
					</div>
				)}
			</ReactQueryWrapperBase>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={instrumentBuilder.isDirty}
				pathToNotBlock={pathToNotBlock}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
}

export default ManualEditComposition;
